import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import { UsersRound } from 'lucide-react';
import * as React from 'react';

import {
  ChoiceSelect,
  CondensedTip,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  useCondensed
} from '@/design';
import { useCan } from '@/domains/auth';
import type {
  IWorkItemAssignee,
  IWorkItemAssignment,
  IWorkItemAssignments,
  IWorkItemMatch,
  IWorkItemMove,
  IWorkItemQuery
} from '../../api/work-item-contracts';
import { workItemsApi } from '../../api/work-items-api';
import { refusalMessage } from '../../refusal';
import { countOf } from '../../lists';
import { BulkAssign } from '../dd/dd-calls-page';
import { NOBODY, sentence } from '../lists/assign-all';
import { NOT_PERMITTED_ASSIGN, NOT_PERMITTED_CHANGE } from './work-item-writes';

/**
 * Assigning from the compliance queue's list (SR-466): the ticked items on the selection bar
 * (DD's `BulkAssign`), and **Assign all N…** beside Columns — every item the list's query matches,
 * every page of it, sent as the query itself. Both are survey-api's
 * `POST /admin/work-items/assignments`, all or nothing, under `responses.annotate` and
 * `work_items.assign`.
 *
 * **Past 2,000 items nothing is sent** (survey-api's `WorkItemService.MaxAssign`): the dialog says so
 * off the list's count, and survey-api's 422 at `filter` is said in its words all the same, since
 * the queue may have grown since.
 *
 * **Undo puts each moved item back as the answer found it** (SR-459's pattern): the answer's
 * `moved` — the assignee before, the revision after — sent back as `restore`, so an item a
 * colleague moved since is left as theirs and said so.
 */

/** An assignment's outcome, as the line above the list says it; `moved` is what Undo sends back. */
export interface IAssignedSaid {
  text: string;
  refused: boolean;
  moved?: IWorkItemMove[];
}

/** survey-api's `WorkItemService.MaxAssign`. */
export const ASSIGN_LIMIT = 2000;

/** What the reader lacks to assign, in `NotPermitted`'s words, or null. */
export function useAssignMissing(): Nullable<string> {
  const can = useCan();
  return !can('responses.annotate') ? NOT_PERMITTED_CHANGE : !can('work_items.assign') ? NOT_PERMITTED_ASSIGN : null;
}

/** The list's query as an assignment's filter: its filters, not its order, fields or window. */
export function matchOf(query: IWorkItemQuery): IWorkItemMatch {
  const { survey, state, trigger, assignee, participantId, refreshRunId, flag, since, q } = query;
  return { survey, state, trigger, assignee, participantId, refreshRunId, flag, since, q };
}

/** An assignment's outcome as one sentence. */
function assignedSentence(done: IWorkItemAssignments, whom: Nullable<string>): string {
  return sentence([
    `${itemsOf(done.assigned)} ${whom ? `assigned to ${whom}` : 'unassigned'}`,
    done.unchanged > 0 && `${done.unchanged.toLocaleString('en-US')} already ${whom ? 'theirs' : 'unassigned'}`
  ]);
}

const itemsOf = (count: number) => countOf(count, 'item');

async function assign(
  body: Extract<IWorkItemAssignment, { assigneeAccountId: unknown }>,
  assignees: IWorkItemAssignee[]
): Promise<IAssignedSaid> {
  const whom = assignees.find((account) => account.accountId === body.assigneeAccountId)?.email ?? null;
  try {
    const done = await workItemsApi.assignWorkItems(body);
    return { text: assignedSentence(done, whom), refused: false, moved: done.moved };
  } catch (error) {
    return { text: refusalMessage(error, 'The items could not be assigned.'), refused: true };
  }
}

/** The selection bar's Assign: the ticked items to one account, or to nobody. */
export function SelectedAssign({
  ids,
  assignees,
  assigneesUnread,
  missing,
  reasonId,
  onAssigned
}: {
  ids: string[];
  assignees: IWorkItemAssignee[];
  assigneesUnread: boolean;
  missing: Nullable<string>;
  reasonId: string;
  onAssigned: (said: IAssignedSaid) => void;
}) {
  return (
    <BulkAssign
      count={ids.length}
      accounts={assignees}
      accountsUnread={assigneesUnread}
      off={missing !== null}
      locked={missing !== null}
      reasonId={reasonId}
      onAssign={async (accountId) => onAssigned(await assign({ itemIds: ids, assigneeAccountId: accountId }, assignees))}
    />
  );
}

/** *Assign all N…*: a dialog choosing the account, then every item the query matches. */
export function AssignAllItems({
  match,
  count,
  assignees,
  missing,
  reasonId,
  onAssigned
}: {
  match: IWorkItemMatch;
  /** The items the list's query matched at its last read; null while unread. */
  count: Nullable<number>;
  assignees: IWorkItemAssignee[];
  missing: Nullable<string>;
  reasonId: string;
  onAssigned: (said: IAssignedSaid) => void;
}) {
  const [open, setOpen] = React.useState(false);
  const [whom, setWhom] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [refused, setRefused] = React.useState<Nullable<string>>(null);
  const labelId = React.useId();
  const { condensed } = useCondensed();
  const n = count ?? 0;
  const tooMany = n > ASSIGN_LIMIT;
  const items = itemsOf(n);
  const email = assignees.find((account) => account.accountId === whom)?.email ?? null;
  const label = `Assign all ${n.toLocaleString('en-US')}…`;

  const send = async () => {
    setBusy(true);
    setRefused(null);
    const said = await assign({ filter: match, assigneeAccountId: whom === NOBODY ? null : whom }, assignees);
    setBusy(false);
    if (said.refused) {
      setRefused(said.text);
      return;
    }
    setOpen(false);
    setWhom('');
    onAssigned(said);
  };

  return (
    <>
      <CondensedTip label={label}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-assign-all-items=""
          className={condensed ? 'size-8 p-0' : undefined}
          aria-label={condensed ? label : undefined}
          disabled={missing !== null || n === 0}
          aria-describedby={missing !== null ? reasonId : undefined}
          onClick={() => {
            setRefused(null);
            setOpen(true);
          }}
        >
          {condensed ? <UsersRound className="size-4" aria-hidden /> : label}
        </Button>
      </CondensedTip>
      <Dialog open={open} onOpenChange={(next) => !busy && setOpen(next)}>
        <DialogContent data-assign-all-dialog="">
          <DialogHeader>
            <DialogTitle>Assign all {items}</DialogTitle>
            <DialogDescription>
              {whom === ''
                ? `The list holds ${items}, on every page.`
                : whom === NOBODY
                  ? `Unassign ${items}?`
                  : `Assign ${items} to ${email ?? 'the account chosen'}?`}
            </DialogDescription>
          </DialogHeader>
          <DialogBody
            as="form"
            onSubmit={(event) => {
              event.preventDefault();
              if (whom !== '' && !tooMany) void send();
            }}
          >
            <div className="grid gap-1.5 text-sm">
              <span id={labelId} className="font-medium">
                Assign to
              </span>
              <ChoiceSelect
                labelledBy={labelId}
                value={whom}
                onChange={setWhom}
                disabled={busy}
                choices={[
                  { value: NOBODY, label: 'Nobody' },
                  ...assignees.map((account) => ({ value: account.accountId, label: account.email }))
                ]}
              />
            </div>
            {tooMany && (
              <p role="alert" className="text-sm text-destructive">
                Narrow the list: {items} match, and one assignment takes at most{' '}
                {ASSIGN_LIMIT.toLocaleString('en-US')}.
              </p>
            )}
            {refused && (
              <p role="alert" className="text-sm text-destructive">
                {refused}
              </p>
            )}
            <DialogFooter className="px-0 pb-0">
              <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy || whom === '' || tooMany}>
                {busy ? 'Assigning…' : 'Assign'}
              </Button>
            </DialogFooter>
          </DialogBody>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** An assignment's outcome above the list, with *Undo* — pressed once — until dismissed. */
export function AssignedSaid({
  said,
  onUndone,
  onDismiss
}: {
  said: IAssignedSaid;
  /** After the undo's write: read the list again. */
  onUndone: () => void;
  onDismiss: () => void;
}) {
  const [undoing, setUndoing] = React.useState(false);
  const [undone, setUndone] = React.useState<Nullable<string>>(null);
  const [failed, setFailed] = React.useState<Nullable<string>>(null);
  const moved = said.moved ?? [];
  // The press that made it is gone with the selection bar: the line takes the focus.
  const line = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => line.current?.focus(), []);

  const undo = async () => {
    setUndoing(true);
    setFailed(null);
    try {
      const done = await workItemsApi.assignWorkItems({
        restore: moved.map((move) => ({
          itemId: move.id,
          assigneeAccountId: move.previousAssigneeAccountId,
          revision: move.revision
        }))
      });
      const back = done.assigned + done.unchanged;
      setUndone(
        sentence([
          `Undone: ${itemsOf(back)} back as they were`,
          done.left > 0 && `${done.left.toLocaleString('en-US')} left as a colleague changed them since`
        ])
      );
      onUndone();
    } catch (error) {
      setFailed(refusalMessage(error, 'The assignment could not be undone.'));
      setUndoing(false);
    }
  };

  return (
    <div
      ref={line}
      tabIndex={-1}
      data-items-assigned=""
      className={cn(
        'flex flex-wrap items-center gap-2 rounded-md border px-3 py-2 text-[0.8125rem] outline-none',
        said.refused
          ? 'border-destructive/40 bg-destructive/5 text-destructive'
          : 'border-border bg-[color-mix(in_srgb,var(--color-teal)_8%,white)]'
      )}
    >
      <span role={said.refused ? 'alert' : 'status'}>{undone ?? said.text}</span>
      {failed && <span role="alert">{failed}</span>}
      {undone === null && moved.length > 0 && (
        <Button type="button" variant="outline" size="sm" disabled={undoing} onClick={() => void undo()}>
          Undo
        </Button>
      )}
      <Button type="button" variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    </div>
  );
}
